/**
 * FirstLoginWizard — iter100.315
 *
 * A friendly 3-step modal that runs the *very first time* a slim-created
 * doctor lands on their dashboard. Walks them through the three highest-
 * signal fields their public microsite needs:
 *
 *   1) Profile photo   → POST /api/doctor/profile/photo   (multipart)
 *   2) About / story   → PUT  /api/doctor/profile          {bio}
 *   3) Public phone    → PUT  /api/doctor/profile          {landing_cta_phone}
 *
 * On finish (or on any dismiss path — Skip / ✕), the wizard calls
 * POST /api/doctor/onboard {} which stamps `db.doctors.onboarded_at` so
 * it never re-appears for the same doctor, even from a different browser.
 *
 * Renders NOTHING if `doctor.onboarded_at` is already set. Renders NOTHING
 * for doctors who somehow already have all three fields filled (their
 * dashboard's auto-stamp effect will mark them onboarded on next load).
 */
import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import PhoneInput10 from "@/components/site/PhoneInput10";
import { toE164, isValid10Digit } from "@/lib/phone";
import { UploadSimple, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";


const TOTAL_STEPS = 3;


function ProgressDots({ current }) {
    return (
        <div className="flex items-center justify-center gap-2 mb-4">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                        i < current
                            ? "w-8 bg-emerald-500"
                            : i === current
                                ? "w-8 bg-primary"
                                : "w-2 bg-slate-200"
                    }`}
                />
            ))}
        </div>
    );
}


function StepShell({ title, subtitle, children, footer }) {
    return (
        <div className="space-y-4">
            <div className="text-center">
                <h3 className="text-lg font-bold text-foreground" data-testid="wizard-step-title">
                    {title}
                </h3>
                {subtitle && (
                    <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
                )}
            </div>
            {children}
            {footer}
        </div>
    );
}


export default function FirstLoginWizard({ doctor, onComplete }) {
    // Guard: only fire for freshly-created doctors who haven't been onboarded.
    const shouldFire = Boolean(doctor) && !doctor.onboarded_at;
    const [open, setOpen] = useState(shouldFire);
    const [step, setStep] = useState(0);

    // Step-local state, seeded from the doctor record.
    const [photoUrl, setPhotoUrl] = useState(doctor?.image || "");
    const [uploading, setUploading] = useState(false);
    const [bio, setBio] = useState(doctor?.bio || "");
    const [phone10, setPhone10] = useState(() => {
        const raw = (doctor?.landing_cta_phone || doctor?.phone || "").replace(/[^\d]/g, "");
        return raw.slice(-10);
    });
    const [saving, setSaving] = useState(false);

    // Sync open state if the doctor prop changes (e.g. after profile refresh).
    useEffect(() => {
        setOpen(Boolean(doctor) && !doctor.onboarded_at);
    }, [doctor?.id, doctor?.onboarded_at]);

    if (!shouldFire) return null;

    /* --------------------------------------------------------------
       Persist the "onboarded_at" stamp on any exit path so the wizard
       never re-appears — even if the doctor skipped every step. The
       endpoint accepts an empty payload and only stamps the timestamp.
       ------------------------------------------------------------ */
    const stampAndClose = async () => {
        try {
            await api.post("/doctor/onboard", {});
        } catch {
            /* Non-blocking — worst case the wizard shows again once. */
        }
        setOpen(false);
        if (onComplete) onComplete();
    };

    /* --------------------------------------------------------------
       Step 1 — Photo
       ------------------------------------------------------------ */
    const uploadPhoto = async (file) => {
        if (!file) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Use JPEG, PNG or WebP");
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Image must be under 2 MB");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/doctor/profile/photo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPhotoUrl(data.image);
            toast.success("Photo uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Photo upload failed");
        } finally {
            setUploading(false);
        }
    };

    /* --------------------------------------------------------------
       Step 2 — Story / bio
       ------------------------------------------------------------ */
    const saveBio = async () => {
        const trimmed = bio.trim();
        if (!trimmed) {
            toast.error("Add a short story — it's the first thing patients read.");
            return false;
        }
        setSaving(true);
        try {
            await api.put("/doctor/profile", { bio: trimmed });
            return true;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save your story");
            return false;
        } finally {
            setSaving(false);
        }
    };

    /* --------------------------------------------------------------
       Step 3 — Public phone
       ------------------------------------------------------------ */
    const savePhone = async () => {
        if (!phone10) return false;                       // Skip path — no error
        if (!isValid10Digit(phone10)) {
            toast.error("Enter a valid 10-digit Indian phone number");
            return false;
        }
        setSaving(true);
        try {
            await api.put("/doctor/profile", { landing_cta_phone: toE164(phone10) });
            return true;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save your public phone");
            return false;
        } finally {
            setSaving(false);
        }
    };

    /* --------------------------------------------------------------
       Advance handler — validates the current step, then either goes
       to the next step or (on the final step) stamps + closes.
       ------------------------------------------------------------ */
    const advance = async () => {
        if (step === 0) {
            // Photo is optional but strongly encouraged; if none set the
            // "Continue" button becomes a "Skip photo" affordance.
            setStep(1);
            return;
        }
        if (step === 1) {
            const ok = await saveBio();
            if (ok) setStep(2);
            return;
        }
        if (step === 2) {
            if (phone10) {
                const ok = await savePhone();
                if (!ok) return;
            }
            await stampAndClose();
            toast.success("You're all set. Your public profile is looking great!");
        }
    };

    /* Small helper so a user can always bail out ("Skip this step") without
       breaking the flow. Final step's skip == same as clicking Finish (skip
       phone, stamp onboarded_at, close). */
    const skipStep = async () => {
        if (step < TOTAL_STEPS - 1) {
            setStep(step + 1);
        } else {
            await stampAndClose();
        }
    };

    const primaryLabel = (() => {
        if (step === 0) return photoUrl ? "Continue" : "Skip photo for now";
        if (step === 1) return "Continue";
        return phone10 ? "Finish setup" : "Finish (skip phone)";
    })();

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                // If the doctor closes the modal via ✕ or ESC, still stamp
                // onboarded_at so the wizard doesn't nag on next mount.
                if (!next) stampAndClose();
            }}
        >
            <DialogContent className="max-w-md" data-testid="first-login-wizard">
                <DialogHeader>
                    <DialogTitle className="text-center text-xl">
                        Welcome, Dr. {(doctor?.name || "").replace(/^Dr\.\s*/, "").split(" ")[0] || "Doctor"} 👋
                    </DialogTitle>
                    <DialogDescription className="text-center">
                        A quick 60-second setup so your public microsite looks great from day one.
                    </DialogDescription>
                </DialogHeader>

                <ProgressDots current={step} />

                {step === 0 && (
                    <StepShell
                        title="Add your photo"
                        subtitle="A friendly headshot builds trust before the first click."
                        footer={null}
                    >
                        <label
                            htmlFor="wizard-photo-input"
                            className="block cursor-pointer"
                            data-testid="wizard-photo-label"
                        >
                            <div className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-2xl p-6 hover:border-primary transition-colors">
                                {photoUrl ? (
                                    <img
                                        src={photoUrl}
                                        alt="Your headshot"
                                        className="h-28 w-28 rounded-full object-cover shadow-md"
                                    />
                                ) : (
                                    <div className="h-28 w-28 rounded-full bg-slate-100 flex items-center justify-center">
                                        <UploadSimple size={28} weight="duotone" className="text-slate-400" />
                                    </div>
                                )}
                                <span className="mt-3 text-sm font-semibold text-primary">
                                    {photoUrl ? "Change photo" : "Choose a photo"}
                                </span>
                                <span className="text-[11px] text-muted-foreground mt-1">
                                    JPEG · PNG · WebP · up to 2 MB
                                </span>
                            </div>
                            <input
                                id="wizard-photo-input"
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                className="hidden"
                                onChange={(e) => uploadPhoto(e.target.files?.[0])}
                                data-testid="wizard-photo-input"
                            />
                        </label>
                    </StepShell>
                )}

                {step === 1 && (
                    <StepShell
                        title="Tell patients about you"
                        subtitle="2–3 sentences on your approach, experience, or what makes your clinic special."
                        footer={null}
                    >
                        <Textarea
                            value={bio}
                            onChange={(e) => setBio(e.target.value)}
                            placeholder="I've helped over 300 patients discover their smile through orthodontics. My clinic focuses on gentle, invisible aligners — no metal, no braces."
                            rows={5}
                            className="resize-none"
                            data-testid="wizard-bio-input"
                        />
                        <div className="flex justify-between text-[11px] text-muted-foreground">
                            <span>{bio.trim().length} characters</span>
                            <span>Tip: mention years of practice + a specialty</span>
                        </div>
                    </StepShell>
                )}

                {step === 2 && (
                    <StepShell
                        title="Public phone for patients"
                        subtitle="The number patients tap to call or WhatsApp you from your microsite. Different from your OTP login."
                        footer={null}
                    >
                        <PhoneInput10
                            value={phone10}
                            onChange={setPhone10}
                            testid="wizard-phone-input"
                            inputClassName="h-11 text-base"
                        />
                        <p className="text-[11px] text-muted-foreground">
                            Leave blank to reuse your login phone (recommended if it&apos;s the same number).
                        </p>
                    </StepShell>
                )}

                <div className="flex items-center justify-between pt-2 mt-2 border-t">
                    <button
                        type="button"
                        onClick={skipStep}
                        className="text-xs font-semibold text-muted-foreground hover:text-foreground"
                        data-testid="wizard-skip-btn"
                        disabled={saving || uploading}
                    >
                        Skip this step
                    </button>
                    <Button
                        onClick={advance}
                        disabled={saving || uploading}
                        className="rounded-full"
                        data-testid="wizard-next-btn"
                    >
                        {saving || uploading ? "Saving…" : primaryLabel}
                        {step < TOTAL_STEPS - 1 ? (
                            <ArrowRight size={14} weight="bold" className="ml-1.5" />
                        ) : (
                            <CheckCircle size={14} weight="bold" className="ml-1.5" />
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
